export type CardKey =
  | "basics"
  | "connection"
  | "execution"
  | "cloud"
  | "container"
  | "directories"
  | "variables"
  | "access"
  | "agent"
  | "dataFolders"
  | "apps"
  | "containerExecution";

export type CardOpenState = Record<CardKey, boolean>;

export const CARD_KEYS: CardKey[] = [
  "basics",
  "connection",
  "execution",
  "cloud",
  "container",
  "directories",
  "variables",
  "access",
  "agent",
  "dataFolders",
  "apps",
  "containerExecution",
];

export const CARD_STATE_STORAGE_KEY = "foundry.runEnvironmentCards.v1";

const readRememberedCardState = (): Partial<CardOpenState> => {
  try {
    const raw = window.localStorage.getItem(CARD_STATE_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const remembered: Partial<CardOpenState> = {};
    for (const key of CARD_KEYS) {
      if (typeof parsed[key] === "boolean") {
        remembered[key] = parsed[key] as boolean;
      }
    }
    return remembered;
  } catch {
    // A corrupt entry, or a browser that refuses storage, must never stop the
    // drawer from rendering. Fall back to the defaults.
    return {};
  }
};

/**
 * Spec assumption A2. Basics and Connection follow the mode, because what a
 * user needs first differs between creating and editing. Every other card
 * starts closed and then remembers whatever the user last left it as.
 */
export const initialCardState = (isEdit: boolean): CardOpenState => {
  const remembered = readRememberedCardState();
  return {
    basics: true,
    connection: !isEdit,
    execution: remembered.execution ?? false,
    cloud: remembered.cloud ?? false,
    container: remembered.container ?? false,
    directories: remembered.directories ?? false,
    variables: remembered.variables ?? false,
    access: remembered.access ?? false,
    agent: remembered.agent ?? false,
    dataFolders: remembered.dataFolders ?? false,
    apps: remembered.apps ?? false,
    containerExecution: remembered.containerExecution ?? false,
  };
};

export const rememberCardState = (state: CardOpenState): void => {
  try {
    window.localStorage.setItem(CARD_STATE_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Private browsing and quota errors are not worth surfacing in a form.
  }
};
